import type { Bot, User } from '../types/api.types';

/**
 * Applies user profile settings (custom_instructions and creativity_level) to a bot
 * if the bot is the default assistant. User settings override the assistant's own settings.
 *
 * @param bot - The bot to apply settings to
 * @param user - The user object containing custom_instructions and creativity_level
 * @returns A new bot object with user settings applied (if applicable)
 */
export function applyUserSettingsToBot(
  bot: Bot,
  user: User | null | undefined
): Bot {
  // Only apply user settings to default assistants
  if (!bot.is_default || !user) {
    return bot;
  }

  const updatedBot: Bot = { ...bot };

  // Override system_instructions if user has custom_instructions
  if (user.custom_instructions && user.custom_instructions.trim() !== '') {
    updatedBot.system_instructions = user.custom_instructions;
  }

  // Override temperature if user has creativity_level
  if (user.creativity_level && user.creativity_level.trim() !== '') {
    // Convert creativity_level string (e.g., "70") to float (0.7)
    const creativityLevel = parseFloat(user.creativity_level);
    if (
      !isNaN(creativityLevel) &&
      creativityLevel >= 0 &&
      creativityLevel <= 100
    ) {
      updatedBot.temperature = creativityLevel / 100;
    }
  }

  return updatedBot;
}
